import * as hardware from "@client/hardware";
import {classNames} from "@reactivated";
import {motion} from "motion/react";
import React from "react";

// The glass two-way (or n-way) pill with the indigo puck springing between
// options. `compact` is the header-corner size: small and tight.
export const Segmented = <T extends string>({
    value,
    options,
    onChange,
    labelledBy,
    label,
    size = "regular",
}: {
    value: T | null;
    options: readonly {value: T; label: string}[];
    onChange: (value: T) => void;
    labelledBy?: string;
    label?: string;
    size?: "regular" | "compact";
}) => {
    const layoutId = React.useId();
    const compact = size === "compact";
    return (
        <div
            role="radiogroup"
            aria-labelledby={labelledBy}
            aria-label={label}
            className={classNames(
                "flex bg-white/55 shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.9)]",
                compact ? "rounded-full p-[3px]" : "rounded-[14px] p-1",
            )}
        >
            {options.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={option.value === value}
                    onClick={() => {
                        void hardware.haptic();
                        onChange(option.value);
                    }}
                    className={classNames(
                        // Pairs split the pill evenly; longer rows give each
                        // option its own width so a long name does not wrap.
                        "relative font-medium whitespace-nowrap",
                        options.length > 2 ? "flex-auto" : "flex-1",
                        compact
                            ? "px-2.5 py-1 text-[10.5px]"
                            : "px-2 py-2.5 text-[13.5px] leading-tight",
                    )}
                >
                    {option.value === value && (
                        <motion.span
                            layoutId={layoutId}
                            className="absolute inset-0 bg-[oklch(0.51_0.21_278)]"
                            style={{borderRadius: compact ? 9999 : 11}}
                            transition={{type: "spring", stiffness: 500, damping: 38}}
                        />
                    )}
                    <span
                        className={classNames(
                            "relative",
                            option.value === value
                                ? "text-white"
                                : "text-[oklch(0.45_0.03_280)]",
                        )}
                    >
                        {option.label}
                    </span>
                </button>
            ))}
        </div>
    );
};
